<template>
  <div
    ref="container"
    class="proximity-fade"
    :class="{ 'is-visible': isVisible, 'is-exiting': isExiting }"
    :style="{ opacity: computedOpacity }"
  >
    <slot />
  </div>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted, onUnmounted } from 'vue'
import { useElementBounding } from '@vueuse/core'

// Generate unique ID for this component
const componentId = Math.random().toString(36).substring(2, 9)

// Inject tracking coordinates from parent
const tracking = inject('proximityTracking', null)

// Track this element's position
const container = ref(null)
const { x, y, width, height } = useElementBounding(container)

// Calculate center of this element
const centerX = computed(() => x.value + width.value / 2)
const centerY = computed(() => y.value + height.value / 2)

// Distance calculation functions
function distanceToMouseY(mouseY, centerY) {
  return Math.abs(mouseY - centerY)
}

function distanceToViewportCenter(itemCenterY, viewportCenterY) {
  return Math.abs(itemCenterY - viewportCenterY)
}

// Calculate raw distance (for winner selection)
const rawDistance = computed(() => {
  if (!tracking) return Infinity

  if (tracking.isMobile.value) {
    return distanceToViewportCenter(centerY.value, tracking.viewportCenterY.value)
  } else {
    return distanceToMouseY(tracking.mouseY.value, centerY.value)
  }
})

// Use raw distance for tracking (to find the actual closest)
const currentDistance = computed(() => rawDistance.value)

// Update distance in shared state whenever it changes
watch(currentDistance, (distance) => {
  if (tracking) {
    tracking.updateDistance(componentId, distance)
  }
}, { immediate: true })

// Register/unregister this component
onMounted(() => {
  if (tracking) {
    tracking.register(componentId)
  }
})

onUnmounted(() => {
  if (tracking) {
    tracking.unregister(componentId)
  }
})

// Track visibility state for animations
const isVisible = ref(false)
const isExiting = ref(false)

// Computed opacity based on whether this is the closest component
const computedOpacity = computed(() => {
  if (!tracking) return 1.0

  const myDistance = rawDistance.value
  const closest = tracking.closestDistance.value

  // Only show if this is THE closest
  const isClosest = Math.abs(myDistance - closest) < 1

  if (isClosest) {
    // Apply buffer zone for opacity
    const bufferZone = height.value * 2  // 2x arrow height for full opacity
    const adjustedDistance = Math.max(0, myDistance - bufferZone)

    const minDist = 0
    const maxDist = 150

    if (adjustedDistance <= minDist) return 1.0
    if (adjustedDistance >= maxDist) return 0.0

    return 1 - (adjustedDistance - minDist) / (maxDist - minDist)
  }

  return 0.0 // Not the closest, hide completely
})

// Watch opacity changes to trigger animations
watch(computedOpacity, (newOpacity, oldOpacity) => {
  const wasInvisible = oldOpacity === 0
  const nowVisible = newOpacity > 0
  const wasVisible = oldOpacity > 0
  const nowInvisible = newOpacity === 0

  // Entering: was 0, now > 0
  if (wasInvisible && nowVisible) {
    isVisible.value = true
    isExiting.value = false
  }

  // Exiting: was > 0, now 0
  if (wasVisible && nowInvisible) {
    isExiting.value = true
    // Remove classes after animation completes
    setTimeout(() => {
      isVisible.value = false
      isExiting.value = false
    }, 600)
  }
})
</script>

<style scoped>
.proximity-fade {
  width: 100%;
  height: 100%;
  transition: opacity 0.6s ease, transform 0.6s ease;
  transform: translateX(-0.67rem);
}

.proximity-fade.is-visible {
  transform: translateX(0);
}

.proximity-fade.is-exiting {
  transform: translateX(0.67rem);
}
</style>
